import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { haptic } from '../../lib/haptic';

type Props = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
};

export function Sheet({ open, onClose, title, children }: Props) {
  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleBackdropClick = () => {
    haptic.tap();
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center" role="dialog" aria-modal="true">
      {/* Backdrop — a solid dim. We intentionally do NOT use backdrop-filter:
          blur here: on iOS Safari the blur layer mis-composites inside a
          portaled fixed overlay (the dim appears offset / not covering the
          whole screen). A plain semi-opaque scrim covers the viewport
          reliably in every browser. */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      {/* Sheet body — capped at 88vh and column-flexed so a tall sheet (release
          readings, duty-slip sign-off) scrolls its content internally instead
          of overflowing the top of the screen. Short sheets still hug their
          content. The grab handle + title stay pinned; only the body scrolls. */}
      <div
        className="relative w-full max-w-md max-h-[88vh] flex flex-col bg-[var(--color-surface,#fff)] rounded-t-[1.75rem] shadow-2xl pb-[env(safe-area-inset-bottom)] animate-[slide-up_0.25s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grab handle */}
        <div className="flex justify-center pt-3 pb-1 flex-none">
          <div className="w-12 h-1 rounded-full bg-[var(--color-border,#d1d5db)]" aria-hidden="true" />
        </div>
        {title && (
          <h2 className="px-6 pt-2 pb-3 text-lg font-semibold text-[var(--color-text)] flex-none">{title}</h2>
        )}
        <div className="px-6 pb-6 min-h-0 overflow-y-auto">{children}</div>
      </div>
      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>,
    document.body,
  );
}
